import { ChangeEventHandler, FC, useEffect, useRef } from 'react';
import { Button, Col, Divider, Input, InputRef, Row, Space } from 'antd';
import { capitalCase } from 'change-case';
import { TbBinaryTree2, TbFocusCentered, TbInputSpark, TbSearch, TbSlash, TbTextRecognition } from 'react-icons/tb';
import { useLocation } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useSlashSearchFocus } from '@/hooks/useSlashSearchFocus';
import {
    SEARCH_HORIZONTAL_GUTTER,
    searchTabDefinitions,
} from '@/layouts/02-Authenticated/layout/03-Content/routes/04-Search/searchTabs';
import type { SearchMatchMode, SearchMode } from '@/queries';
import { DropdownButtonSelect, DropdownMenuLabel, LearnMoreFooter, PlanTag, SmallTooltip } from '@/shared';
import { SetStateMethod } from '@/types';

interface SearchInputProps {
    input: string;
    matchMode: SearchMatchMode;
    searchMode: SearchMode;
    setInput: SetStateMethod<string>;
    setMatchMode: (matchMode: SearchMatchMode) => void;
    setSearchMode: (searchMode: SearchMode) => void;
    selectedIndex: number;
    setSelectedIndex: SetStateMethod<number>;
}

const SearchInput: FC<SearchInputProps> = ({
    input,
    matchMode,
    searchMode,
    setInput,
    setMatchMode,
    setSearchMode,
    setSelectedIndex,
}): JSX.Element => {
    const location = useLocation();
    const { hasMinimumPlan } = useEntitlements();
    const requestedTabKey = location.pathname.split('/')[2];
    const tabsActiveKey = searchTabDefinitions.some((tab) => tab.key === requestedTabKey) ? requestedTabKey : undefined;
    const inputRef = useRef<InputRef>(null);
    const searchInputVisible = tabsActiveKey !== 'events';
    useSlashSearchFocus(inputRef, searchInputVisible, { priority: 20 });
    const handleSearchChange: ChangeEventHandler<HTMLInputElement> = (event) => {
        setInput(event.target.value);
        setSelectedIndex(0);
    };

    const { token } = useAntDesignTheme();
    const supportsMatchMode = tabsActiveKey !== 'events';
    const supportsSemanticMode = tabsActiveKey === 'all' || tabsActiveKey === 'companies';
    const canUseAdvancedSearch = hasMinimumPlan('pro');
    const changeMatchMode = (nextMatchMode: SearchMatchMode) => {
        setMatchMode(nextMatchMode);
        setSelectedIndex(0);
    };
    const changeSearchMode = (nextSearchMode: SearchMode | undefined) => {
        if (!nextSearchMode || (!canUseAdvancedSearch && nextSearchMode !== 'keyword')) {
            return;
        }

        setSearchMode(nextSearchMode);
        setSelectedIndex(0);
    };

    useEffect(() => {
        if (!canUseAdvancedSearch && searchMode !== 'keyword') {
            setSearchMode('keyword');
        }
    }, [canUseAdvancedSearch, searchMode, setSearchMode]);

    const searchModeOptions = [
        {
            description: canUseAdvancedSearch
                ? 'Combine keyword relevance with interpreted company-screening intent.'
                : 'Combine keyword and semantic retrieval. Requires Pro or higher.',
            disabled: !canUseAdvancedSearch,
            icon: <TbInputSpark />,
            label: 'Automatic',
            metadata: canUseAdvancedSearch ? undefined : [<PlanTag key='pro' plan='pro' />],
            value: 'automatic' as const,
        },
        {
            description: 'Match names, tickers, descriptions, and other indexed text.',
            dividerBefore: true,
            icon: <TbSearch />,
            label: 'Keyword',
            value: 'keyword' as const,
        },
        {
            description: canUseAdvancedSearch
                ? 'Interpret a natural-language company screen and its financial constraints.'
                : 'Interpret natural-language company screens. Requires Pro or higher.',
            disabled: !canUseAdvancedSearch,
            icon: <TbBinaryTree2 />,
            label: 'Semantic',
            metadata: canUseAdvancedSearch ? undefined : [<PlanTag key='pro' plan='pro' />],
            value: 'semantic' as const,
        },
    ];
    const searchPlaceholder =
        supportsSemanticMode && searchMode === 'semantic'
            ? 'Describe The Companies You Want To Find...'
            : supportsSemanticMode && searchMode === 'automatic'
              ? 'Search By Keyword Or Describe The Companies You Want To Find...'
              : tabsActiveKey
                ? `Search ${capitalCase(tabsActiveKey)}...`
                : 'Search...';

    const matchModeControls =
        supportsMatchMode && searchMode !== 'semantic' ? (
            <Space.Compact size='small'>
                <SmallTooltip title='Match results containing the search text'>
                    <Button
                        aria-label='Use partial matching'
                        aria-pressed={matchMode === 'partial'}
                        icon={<TbTextRecognition />}
                        size='small'
                        type={matchMode === 'partial' ? 'primary' : 'default'}
                        onClick={() => changeMatchMode('partial')}
                    >
                        Partial
                    </Button>
                </SmallTooltip>
                <SmallTooltip title='Match an entire name, title, ticker, or other indexed field'>
                    <Button
                        aria-label='Use exact matching'
                        aria-pressed={matchMode === 'exact'}
                        icon={<TbFocusCentered />}
                        size='small'
                        type={matchMode === 'exact' ? 'primary' : 'default'}
                        onClick={() => changeMatchMode('exact')}
                    >
                        Exact
                    </Button>
                </SmallTooltip>
            </Space.Compact>
        ) : null;

    return searchInputVisible ? (
        <div
            style={{
                padding: `0 ${SEARCH_HORIZONTAL_GUTTER}`,
                marginTop: '0.5rem',
            }}
        >
            <Row gutter={[12, 0]}>
                <Col flex='auto'>
                    <Input
                        ref={inputRef}
                        size='middle'
                        style={{
                            marginBottom: 0,
                            paddingLeft: '0.5rem',
                            paddingRight: '0.3rem',
                        }}
                        prefix={<TbSearch style={{ color: token.colorTextQuaternary, marginRight: '0.375rem' }} />}
                        value={input}
                        placeholder={searchPlaceholder}
                        onChange={handleSearchChange}
                        suffix={
                            <Space align='center' size={4} style={{ height: '100%' }}>
                                <SmallTooltip title='Press / to focus Search'>
                                    <span
                                        aria-label='Press slash to focus Search'
                                        role='img'
                                        style={{ alignItems: 'center', display: 'inline-flex', height: '100%' }}
                                    >
                                        <TbSlash
                                            aria-hidden
                                            size='1rem'
                                            style={{
                                                color: token.colorTextQuaternary,
                                                display: 'block',
                                                marginRight: 0,
                                                transform: 'translateY(0.125rem)',
                                            }}
                                        />
                                    </span>
                                </SmallTooltip>
                                {matchModeControls ? <Divider type='vertical' style={{ margin: '0 0.25rem' }} /> : null}
                                {matchModeControls}
                            </Space>
                        }
                    />
                </Col>
                {supportsSemanticMode ? (
                    <Col flex='none'>
                        <DropdownButtonSelect<SearchMode>
                            aria-label='Search mode'
                            footer={<LearnMoreFooter topic='search modes' variant='dropdown' />}
                            header={<DropdownMenuLabel>Search Mode</DropdownMenuLabel>}
                            matchTriggerWidth={false}
                            menuWidth='21rem'
                            options={searchModeOptions}
                            style={{ minWidth: '10rem' }}
                            value={searchMode}
                            onChange={changeSearchMode}
                        />
                    </Col>
                ) : null}
                {/* <div
                    style={{
                        borderBottom: '1px solid #f2f2f2',
                        padding: '0 1.5rem',
                        paddingBottom: '0.5rem',
                    }}
                >
                    <Row>
                        <Col flex='none'>
                            <Button style={{  }} type='dashed' size='small'>
                                <ListFilter
                                    size='0.875rem'
                                    style={{ verticalAlign: '-0.1rem', marginRight: '0.375rem' }}
                                />
                                Filter
                            </Button>
                        </Col>
                        <Col flex='auto' />
                        <Col flex='none'>
                            <Space.Compact>
                                <Button
                                    type='dashed'
                                    size='small'
                                >
                                    <ListFilter
                                        size='0.875rem'
                                        style={{ verticalAlign: '-0.1rem', marginRight: '0.375rem' }}
                                    />
                                    Saved as Custom Filter
                                </Button>
                                <Button
                                    style={{
                                        borderTopRightRadius: '0.25rem',
                                        borderBottomRightRadius: '0.25rem',
                                        padding: '0 0.2rem',
                                    }}
                                    size='small'
                                >
                                    <TbDirection
                                        size={'1rem'}
                                        style={{
                                            color: token.colorTextTertiary,
                                            verticalAlign: '-0.2rem',
                                        }}
                                    />
                                </Button>
                            </Space.Compact>
                            <Divider type='vertical' />
                            <Button
                                size='small'
                                style={{
                                    paddingLeft: '0.75rem',
                                    ,
                                }}
                            >
                                <TbAdjustmentsAlt style={{ verticalAlign: '-0.125rem', marginRight: '0.375rem' }} />
                                Display
                                <TbDirection
                                    size={'1rem'}
                                    style={{
                                        color: token.colorTextTertiary,
                                        verticalAlign: '-0.2rem',
                                        marginLeft: '0.35rem',
                                    }}
                                />
                            </Button>
                        </Col>
                    </Row>
                </div> */}
            </Row>
        </div>
    ) : (
        <></>
    );
};

export default SearchInput;
